import { useEffect, useRef, useState } from "react";
import StudentModel from "../../api/models/StudentModel";
import TutorModel from "../../api/models/TutorModel";
import { classNames } from "../../helpers";
import { useTranslation } from "react-i18next";

export default function DropDownSearchFilter({
  list,
  setStudentTutorFilter,
  selectedIds,
}: {
  list: (StudentModel | TutorModel | any)[];
  setStudentTutorFilter: any;
  selectedIds: number[];
}) {
  const LocalBasePath = "page.supervisor.sessions";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIdsArr, setSelectedIdsArr] = useState(selectedIds);
  const [searchedList, setSearchedList] = useState<
    (StudentModel | TutorModel | any)[]
  >([]);
  // Ref to handle click outside
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const isChecked = (id: number) => {
    return (
      selectedIdsArr?.some((param: any) => {
        return param.id === id;
      }) || selectedIdsArr.some((param) => param == id)
    );
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedIdsArr((prevParams: any) => {
      // Check if the item is already in params
      if (isChecked(id)) {
        // If checked, remove it from params
        return prevParams.filter((param: any) => param !== id);
      } else {
        // If unchecked, add it to params
        return [...prevParams, id];
      }
    });
  };

  const clearOptions = () => {
    setSelectedIdsArr([]);
  };

  const applyFilter = () => {
    setIsOpen(false);
    setStudentTutorFilter(selectedIdsArr);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update searchedList whenever the searchTerm changes
  useEffect(() => {
    if (searchTerm) {
      setSearchedList(
        list.filter((user) =>
          (user.first_name + " " + user.last_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setSearchedList(list); // Reset to full list if no search term
    }
  }, [searchTerm, list]); // Run whenever searchTerm or list changes

  return (
    <div>
      <span className="mt-2 content-box inline-block" onClick={toggleDropdown}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-6 h-6 text-gray-600 cursor-pointer p-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 4h18l-7 10v6l-4-2v-4L3 4z"
          />
        </svg>
      </span>

      {isOpen && (
        <div
          ref={dropdownRef}
          id="dropdownSearch"
          className={classNames(
            "z-10 bg-white rounded-lg shadow absolute w-60"
          )}
        >
          <div className="p-3">
            <label htmlFor="input-group-search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500 "
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 19l-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} // Update searchTerm on input change
                id="input-group-search"
                className="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 "
              />
            </div>
          </div>
          <ul className="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700 ">
            {searchedList.map((user, index) => {
              return (
                <li key={index}>
                  <div className="flex items-center ps-2 rounded hover:bg-gray-100 ">
                    <input
                      id={`checkbox-item-${index}`}
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 "
                      onChange={() => handleCheckboxChange(user.id)}
                      checked={isChecked(user.id)}
                    />
                    <label
                      htmlFor={`checkbox-item-${index}`}
                      className="w-full py-2 ms-2 text-sm font-medium text-gray-900 rounded "
                    >
                      {user["first_name"] + " " + user["last_name"]}
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-between w-full">
            <button
              onClick={clearOptions}
              className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-b-lg hover:bg-gray-100 "
            >
              {t("clear")}
            </button>
            <button
              onClick={applyFilter}
              className="flex items-center p-3 text-sm font-medium text-brandblue-600 rounded-b-lg hover:bg-gray-100 "
            >
              {t("apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
